import { cn } from "@/libraries/cn";
import AddIcon from "@mui/icons-material/Add";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import SyncIcon from "@mui/icons-material/Sync";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import type { StackProps } from "@mui/material/Stack";
import { useTranslation } from "react-i18next";

interface ButtonSpeedDialProps extends StackProps {
	bottom?: number;
	right?: number;
	syncProcessing?: boolean;
	onClickSetting?: React.MouseEventHandler<HTMLButtonElement>;
	onClickAdd?: React.MouseEventHandler<HTMLButtonElement>;
	onClickCamera?: React.MouseEventHandler<HTMLButtonElement>;
	onClickSync?: React.MouseEventHandler<HTMLButtonElement>;
	onClickHemiAi?: React.MouseEventHandler<HTMLButtonElement>;
	classes?: Record<"add", HTMLButtonElement["className"]>;
}

const ButtonSpeedDial = (props: ButtonSpeedDialProps) => {
	const { t } = useTranslation("common");
	const {
		bottom = 50,
		right = 0,
		syncProcessing = false,
		onClickCamera,
		onClickSetting,
		onClickAdd,
		onClickSync,
		onClickHemiAi,
		className,
		classes,
		...stackProps
	} = props;

	if (
		!onClickCamera &&
		!onClickAdd &&
		!onClickSync &&
		!onClickHemiAi &&
		!onClickSetting
	) {
		return null;
	}

	return (
		<Stack
			justifyContent="center"
			alignItems="center"
			position="absolute"
			bottom={bottom}
			right={right}
			mb={8}
			className={cn("!px-4 !py-4 gap-2 md:gap-6", className)}
			{...stackProps}
		>
			{onClickSync ? (
				<IconButton
					size="medium"
					className="!bg-gradient-camera !w-12 !h-12 !text-white !shadow-xl"
					onClick={onClickSync}
					disabled={syncProcessing}
					title={t("common:sync")}
				>
					<SyncIcon
						className={cn("rotate-180", {
							"animate-spin": syncProcessing,
						})}
					/>
				</IconButton>
			) : null}
			{onClickCamera ? (
				<IconButton
					size="medium"
					className="!bg-gradient-camera !w-12 !h-12 !text-white !shadow-xl"
					onClick={onClickCamera}
					title={t("common:camera")}
				>
					<CameraAltIcon />
				</IconButton>
			) : null}
			{onClickHemiAi ? (
				<IconButton
					color="default"
					size="medium"
					className="!bg-[#712EA4] !w-12 !h-12 !text-white !shadow-xl"
					onClick={onClickHemiAi}
					title={t("common:hemiAi")}
				>
					<span className="text-sm font-bold">HEMI</span>
				</IconButton>
			) : null}
			{onClickAdd ? (
				<IconButton
					className={cn(
						"!bg-primary !w-14 !h-14 !text-white !shadow-xl",
						classes?.add,
					)}
					onClick={onClickAdd}
					title={t("common:add")}
				>
					<AddIcon fontSize="large" />
				</IconButton>
			) : null}
			{onClickSetting ? (
				<IconButton
					className="!bg-primary !w-14 !h-14 !text-white !shadow-xl"
					onClick={onClickSetting}
					title={t("common:setting")}
				>
					<SettingsIcon fontSize="large" />
				</IconButton>
			) : null}
		</Stack>
	);
};

export default ButtonSpeedDial;
